import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, forkJoin, Observable, Subject, take, takeUntil, tap } from 'rxjs';
import { LocationService } from 'src/app/services/location.service';
import { PeopleService } from 'src/app/services/people.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AppPermissions } from 'src/app/shared/app.permissions';

@Component({
  selector: 'ness-find-person-results',
  templateUrl: './find-person-results.component.html'
})
export class FindPersonResultsComponent implements OnDestroy {
  @Input() suppressEdit: boolean = false;
  @Output() notifySelectedPeople: EventEmitter<ServerModels.ENESS_PersonDTO[]> = new EventEmitter<ServerModels.ENESS_PersonDTO[]>();

  matchingPeopleResultCols = [
    { field: 'ENESS_XREF_PeopleFBIFileNumbers', header: 'FBI #' },
    { field: 'ENESS_XREF_PeopleStateIDNumbers', header: 'State ID' },
    { field: 'LastName', header: 'Last Name' },
    { field: 'FirstName', header: 'First Name' },
    { field: 'DateOfBirth', header: 'Birth Date' },
    { field: 'SSN', header: 'SSN' }
  ];
  matchingPeopleResult$: Observable<ServerModels.ENESS_PersonDTO[]> = this.peopleService.findPeopleResultSubj.pipe(tap((x: ServerModels.ENESS_PersonDTO[]) => {
    if (x.length === 0) {
      this.selectedPeople = [];
    }
  }));
  defaultTableRows: number = AppConstants.defaultTableRows;
  displaySidebar: boolean = false;
  hasPermissionToEditAddress: boolean = false;
  personAddress: ServerModels.ENESS_AddressDTO;
  personAddressType: string;
  personAddressTypeFromRef: string;
  personAddressTypeOther: string;
  private _personDTO: ServerModels.ENESS_PersonDTO = null;
  personDTO: BehaviorSubject<ServerModels.ENESS_PersonDTO> = new BehaviorSubject(this._personDTO);
  personDetails$: Observable<ServerModels.ENESS_PersonDTO> = this.personDTO.asObservable();
  selectedPeople: ServerModels.ENESS_PersonDTO[] = [];
  showPersonAddressDetails: boolean = false;
  showPersonAddressDetailForm: boolean = false;
  showPersonDetails: boolean = false;
  showLocationView: boolean = false;
  showEditLocationView: boolean = false;

  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(public peopleService: PeopleService,
    public locationService: LocationService,
    private messageService: MessageService) { }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public onSelectPerson(): void {
    this.notifySelectedPeople.emit(this.selectedPeople);
  }

  public onUnselectPerson(): void {
    this.notifySelectedPeople.emit(this.selectedPeople);
  }

  public viewPersonDetails(personId: string): void {
    this.displaySidebar = true;
    this.showPersonAddressDetails = false;
    this.showPersonAddressDetailForm = false;
    this.personAddress = null;
    this.peopleService.getPersonDetailsDTOById(personId).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (person: ServerModels.ENESS_PersonDTO) => {
        this._getPersonLECases(personId);
        this._getPersonInfoReports(personId);
        this._getPersonResults(personId);
        this._personDTO = person;
        this.personDTO.next(this._personDTO);
      },
      error: (err:any) => {
        this.messageService.addAll([{ key: 'viewPersonDetailsMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      },
      complete: () => { this.showPersonDetails = true; }
    });
  }

  public viewAddressDetails(addressDetails: any[]): void {
    this.displaySidebar = true;
    this.showPersonDetails = false;
    this.showPersonAddressDetails = true;
    this.showPersonAddressDetailForm = false;
    this.hasPermissionToEditAddress = addressDetails[1];
    this.personAddressTypeFromRef = addressDetails[2];
    this.personAddressTypeOther = addressDetails[3];
    this.personAddressType = addressDetails[2] === "Other" ? addressDetails[2] + ": " + addressDetails[3] : addressDetails[2];

    forkJoin({
      address:this.locationService.getAddressDetailsById(addressDetails[0]),
      leCases:this.locationService.getAddressLECases(addressDetails[0]),
      people:this.locationService.getAddressPeople(addressDetails[0])
    }).pipe(take(1)).subscribe({
      next: (val: { address: ServerModels.ENESS_AddressDTO, leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[] }) => {
        this.showLocationView = true;
        this.personAddress = val.address;
        this.locationService.addressLECasesSubj.next(val.leCases);
        this.locationService.addressPeopleSubj.next(val.people);
      },
      error: (err:any) => {
        this.messageService.addAll([{ key: 'viewAddressDetailsMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      }
    })
  }

  public editLocation(editLocation: string): void {
    if (editLocation === 'Edit') {
      this.showPersonDetails = false;
      this.showPersonAddressDetails = false;
      this.showPersonAddressDetailForm = true;
      this.showLocationView = false;
      this.showEditLocationView = true;
    }
  }

  public onLocationFormClose(newAddress: ServerModels.ENESS_AddressDTO): void {
    this.viewAddressDetails([newAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
  }

  public onViewPersonDetailsClose(): void {
    this._personDTO = null;
    this.personDTO.next(this._personDTO);
    this.peopleService.personLECasesSubj.next(null);
    this.peopleService.personInfoReportsSubj.next(null);
    this.peopleService.personResultsSubj.next(null);
    this.showPersonDetails = false;
    this.displaySidebar = false;
  }

  public onClickCloseButton(): void {
    if (this.showPersonDetails) {
      this.onViewPersonDetailsClose();
    }
    if (this.showPersonAddressDetails) {
      this.showPersonAddressDetails = false;
      this.showPersonDetails = true;
    }
    if (this.showPersonAddressDetailForm) {
      this.viewAddressDetails([this.personAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
    }
  }

  public closeForm(open: boolean): void {
    if (!open) {
      this.showPersonDetails = false;
    }
  }

  private _getPersonResults(personId: string): void {
    if (AppPermissions.canViewResults) {
      this.peopleService.getPersonResults(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: { 'InvestigativeTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personResultsSubj.next(result),
        (err:any) => {
          this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
        });
    }
  }

  private _getPersonInfoReports(personId: string): void {
    if (AppPermissions.canViewIRs) {
      this.peopleService.getPersonInfoReports(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: { 'IntelligenceTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personInfoReportsSubj.next(result),
        (err:any) => {
          this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
        });
    }
  }

  private _getPersonLECases(personId: string): void {
    this.peopleService.getPersonLECases(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: ServerModels.PersonLECases[]) =>
      this.peopleService.personLECasesSubj.next(result),
      (err:any) => {
        this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      });
  }
}
